if (!customElements.get('e8-collar-media')) {
  customElements.define('e8-collar-media', class extends HTMLElement {
    connectedCallback() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const source = data?.html?.querySelector('e8-collar-media');
        if (!data?.variant || source?.dataset.productId !== this.dataset.productId) return;
        this.dataset.missing = source.dataset.missing;
        const missing = source.dataset.missing === 'true';
        const fallback = this.querySelector('.e8-collar-media__fallback');
        const gallery = this.querySelector('.e8-collar-media__gallery');
        fallback.hidden = !missing;
        gallery.hidden = missing;
        if (missing) fallback.replaceChildren(...source.querySelector('.e8-collar-media__fallback').cloneNode(true).childNodes);
        const visual = source.querySelector('template[data-e8-collar-visual]');
        const craft = Array.from(document.querySelectorAll('e8-collar-craft')).find(element => element.dataset.productId === this.dataset.productId);
        if (!craft) return;
        if (visual) craft.querySelector('[data-collar-craft-visual]').replaceChildren(visual.content.cloneNode(true));
        const hardware = craft.querySelector('[data-hardware-label]');
        const index = Number(craft.dataset.hardwareOption) - 1;
        if (hardware && index >= 0) hardware.textContent = data.variant.options[index].replace('Nickle', 'Nickel');
      });
    }
    disconnectedCallback() { this.unsubscribe?.(); }
  });
}

if (!customElements.get('e8-collar-personalization')) {
  customElements.define('e8-collar-personalization', class extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-engraving-toggle]');
      this.fields = this.querySelector('[data-engraving-fields]');
      this.inputs = Array.from(this.querySelectorAll('[data-engraving-input]'));
      this.fontSelect = this.querySelector('[data-engraving-font]');
      this.preview = this.querySelector('[data-engraving-preview]');
      this.previewName = this.querySelector('[data-preview-name]');
      this.previewPhone = this.querySelector('[data-preview-phone]');
      this.form = this.closest('form');
      if (!this.form || !this.toggle) return;
      this.toggle.disabled = false;
      this.onChange = () => this.updateFields();
      this.onInput = () => {
        this.inputs.forEach(input => input.setCustomValidity(''));
        this.updatePreview();
      };
      this.onFontChange = () => this.updatePreview();
      this.toggle.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.fontSelect.addEventListener('change', this.onFontChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const source = data?.html?.querySelector('e8-collar-personalization');
          if (!source || source.dataset.productId !== this.dataset.productId) return;
          this.dataset.previewColor = source.dataset.previewColor;
          this.dataset.previewMetal = source.dataset.previewMetal;
          this.updatePreview();
        });
      }
      this.updateFields();
    }
    updateFields() {
      const enabled = this.toggle.checked;
      this.fields.hidden = !enabled;
      this.toggle.setAttribute('aria-expanded', String(enabled));
      this.form.dataset.e8HasEngraving = String(enabled);
      this.fontSelect.disabled = !enabled;
      this.inputs.forEach(input => {
        input.disabled = !enabled;
        input.setCustomValidity('');
      });
      if (enabled) this.updatePreview();
    }
    updatePreview() {
      const selected = this.fontSelect.selectedOptions[0];
      this.preview.dataset.font = selected.dataset.font;
      this.preview.dataset.color = this.dataset.previewColor;
      this.preview.dataset.metal = this.dataset.previewMetal;
      const name = this.inputs[0].value.trim();
      const phone = this.inputs[1].value.trim();
      this.previewName.textContent = name || (phone ? '' : this.dataset.sampleText);
      this.previewPhone.textContent = phone;
      this.previewName.setAttribute('y', phone && name ? '83' : '96');
      this.previewPhone.setAttribute('y', name ? '111' : '96');
      const fonts = { modern: '700 32px E8ModernPreview', classic: '400 32px Georgia', signature: '400 38px E8SignaturePreview', bold: '400 38px E8BoldPreview' };
      const sequence = (this.previewSequence || 0) + 1;
      this.previewSequence = sequence;
      const fit = () => {
        if (this.previewSequence !== sequence || !this.isConnected || this.fields.hidden) return;
        for (const [text, maxSize] of [[this.previewName, phone ? 31 : 37], [this.previewPhone, name ? 20 : 29]]) {
          text.style.fontSize = `${maxSize}px`;
          const length = text.getComputedTextLength();
          if (length > 335) text.style.fontSize = `${Math.max(9, maxSize * 335 / length)}px`;
        }
      };
      requestAnimationFrame(fit);
      document.fonts?.load(fonts[selected.dataset.font]).then(fit).catch(() => {});
    }
    validate() {
      if (!this.toggle.checked) return true;
      const valid = this.inputs.some(input => input.value.trim());
      this.inputs[0].setCustomValidity(valid ? '' : this.dataset.requiredMessage);
      if (!valid) this.inputs[0].reportValidity();
      return valid;
    }
    prepareCartData(formData) {
      if (!this.toggle.checked) return;
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value.trim()) properties[match[1]] = value.trim();
      }
      const id = formData.get('id');
      const quantity = Number(formData.get('quantity')) || 1;
      const group = crypto.randomUUID();
      const engravingProperties = {};
      for (const key of ['Engraving name', 'Engraving phone', 'Engraving font']) {
        if (properties[key]) engravingProperties[key] = properties[key];
        delete properties[key];
      }
      properties._e8_engraving_group = group;
      properties._e8_engraving_role = 'collar';
      for (const key of Array.from(formData.keys())) {
        if (key === 'id' || key === 'quantity' || key.startsWith('properties[')) formData.delete(key);
      }
      formData.set('items[0][id]', id);
      formData.set('items[0][quantity]', quantity);
      for (const [key, value] of Object.entries(properties)) formData.set(`items[0][properties][${key}]`, value);
      formData.set('items[1][id]', this.dataset.engravingVariant);
      formData.set('items[1][quantity]', quantity);
      formData.set('items[1][parent_id]', id);
      formData.set('items[1][properties][_e8_engraving_group]', group);
      formData.set('items[1][properties][_e8_engraving_role]', 'service');
      formData.set('items[1][properties][For collar]', this.dataset.productTitle);
      for (const [key, value] of Object.entries(engravingProperties)) formData.set(`items[1][properties][${key}]`, value);
    }
    disconnectedCallback() {
      this.toggle?.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.fontSelect?.removeEventListener('change', this.onFontChange);
      this.unsubscribeVariant?.();
    }
  });
}

if (!customElements.get('e8-collar-purchase')) {
  customElements.define('e8-collar-purchase', class extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.formId);
      const primary = form?.querySelector('button[type="submit"]');
      const secondary = this.querySelector('button');
      if (!primary || !secondary) return;
      const sync = () => {
        secondary.disabled = primary.disabled || primary.getAttribute('aria-disabled') === 'true';
        secondary.setAttribute('aria-busy', String(primary.classList.contains('loading')));
        secondary.querySelector('span').textContent = primary.querySelector('span')?.textContent.trim();
      };
      this.observer = new MutationObserver(sync);
      this.observer.observe(primary, { attributes: true, childList: true, subtree: true });
      sync();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });
}
